import { useState } from 'react'
import ConnectedAccountRow from '@/settings/components/ConnectedAccountRow'
import ConnectedAccountsLoadingRow from '@/settings/components/ConnectedAccountsLoadingRow'
import ConnectRedirectDialog from '@/settings/components/ConnectRedirectDialog'
import DisconnectExternalStorageAccountDialog from '@/settings/components/DisconnectExternalStorageAccountDialog'
import ProviderOptionRow from '@/settings/components/ProviderOptionRow'
import SectionHeader from '@/settings/components/SectionHeader'
import SectionIntro from '@/settings/components/SectionIntro'
import useConnectedAccounts from '@/settings/hooks/useConnectedAccounts'
import { isOrgGatedExternalStorageProvider, kIsExternalStorageProviderEnabled } from '@/constants/constants-external-storage'

export default function ConnectedAccounts() {
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false)
  const {
    accountsLoadError,
    confirmingProvider,
    handleConfirmConnect,
    handleDisconnect,
    handleProviderConnect,
    handleReconnect,
    isInitialLoading,
    loadingProvider,
    providers,
    setConfirmingProvider,
    visibleAccount,
  } = useConnectedAccounts()

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border-default bg-surface-emphasis">
      <SectionHeader />
      {isInitialLoading ? (
        <ConnectedAccountsLoadingRow />
      ) : visibleAccount ? (
        <ConnectedAccountRow
          account={visibleAccount}
          onDisconnect={() => setDisconnectDialogOpen(true)}
          onReconnect={handleReconnect}
          loading={loadingProvider === visibleAccount.provider}
        />
      ) : (
        <>
          <SectionIntro error={accountsLoadError} />
          <div aria-label="Document management system" className="flex flex-col">
            {providers.map((providerKey) => {
              // Clio only reaches this list when the org is allowlisted (see useConnectedAccounts),
              // so any Clio row shown here is connectable even though it isn't env-enabled in prod.
              // An org-gated provider only reaches this list when the hook has
              // already confirmed the viewer's organization is allowlisted, so it
              // is connectable even though it is absent from the env-enabled set.
              const providerEnabled = kIsExternalStorageProviderEnabled(providerKey) || isOrgGatedExternalStorageProvider(providerKey)

              return (
                <ProviderOptionRow
                  key={providerKey}
                  providerKey={providerKey}
                  onSelect={() => {
                    if (providerEnabled) handleProviderConnect(providerKey)
                  }}
                  disabled={!providerEnabled || Boolean(loadingProvider) || accountsLoadError}
                  comingSoon={!providerEnabled}
                />
              )
            })}
          </div>
        </>
      )}

      <ConnectRedirectDialog
        provider={confirmingProvider}
        open={confirmingProvider != null}
        loading={loadingProvider === confirmingProvider}
        onOpenChange={(open) => {
          if (!open && !loadingProvider) setConfirmingProvider(null)
        }}
        onContinue={handleConfirmConnect}
      />

      {visibleAccount && (
        <DisconnectExternalStorageAccountDialog
          account={visibleAccount}
          open={disconnectDialogOpen}
          onOpenChange={setDisconnectDialogOpen}
          onConfirm={handleDisconnect}
        />
      )}
    </div>
  )
}
